import { NuxtAppOptions } from '@nuxt/types'
import { ApolloProvider } from 'vue-apollo'

import refreshTokenMutation from '@/apollo/mutations/auth/refreshToken.gql'

import {
  getCsrfEndpoint,
  appendCookieString,
  getCsrfToken,
  isJWTExpired,
  parseSetCookieHeaders,
  AuthCookieAttributes
} from '~/utils/auth/helper'

interface TokenResponse {
  refreshTokenCookie?: AuthCookieAttributes
  jwtCookie?: AuthCookieAttributes
  refreshed: boolean
}

interface TokenRefresherConfig {
  apolloProvider: ApolloProvider<any>
  csrfEndpoint: string
}

export class TokenRefresher {
  private apollo: ApolloProvider<any>
  private csrfEndpoint: string

  constructor(config: TokenRefresherConfig) {
    if (!config.apolloProvider) {
      throw new Error('Apollo provider is required for TokenRefresher')
    }
    if (!config.csrfEndpoint) {
      throw new Error('CSRF endpoint is required for TokenRefresher')
    }
    this.apollo = config.apolloProvider
    this.csrfEndpoint = config.csrfEndpoint
  }

  public static fromNuxtApp(app: NuxtAppOptions): TokenRefresher {
    if (!app.apolloProvider) {
      throw new Error('Apollo provider is not defined in Nuxt app')
    }
    return new TokenRefresher({
      apolloProvider: app.apolloProvider,
      csrfEndpoint: getCsrfEndpoint(app.$config)
    })
  }

  public async refreshIfNeeded(cookies: Record<string, string>): Promise<TokenResponse> {
    const currentJWT = cookies['JWT']
    const currentRefreshToken = cookies['JWT-refresh-token']

    if (currentJWT && !isJWTExpired(currentJWT)) {
      return {
        refreshed: false
      }
    }

    if (!currentRefreshToken) {
      throw new Error('Cannot refresh token: refresh token is missing')
    }

    return await this.refresh(cookies, currentRefreshToken)
  }

  private async refresh(
    cookies: Record<string, string>,
    refreshToken: string
  ): Promise<TokenResponse> {
    try {
      const csrfToken = await getCsrfToken(cookies, this.csrfEndpoint)
      const cookieString = appendCookieString(
        appendCookieString('', 'csrftoken', csrfToken),
        'JWT-refresh-token',
        refreshToken
      )

      const headers: Record<string, string> = {
        'X-CSRFToken': csrfToken,
        Cookie: cookieString
      }

      const responseHeadersRef: Record<string, string[]> = {}
      const context = {
        headers,
        responseHeadersRef
      }
      await this.apollo.defaultClient.mutate({
        mutation: refreshTokenMutation,
        variables: {},
        context
      })

      const responseCookies = parseSetCookieHeaders(responseHeadersRef['set-cookie'] as string[])

      if (!responseCookies['JWT']?.value || !responseCookies['JWT-refresh-token']?.value) {
        throw new Error('Invalid response: missing required tokens')
      }

      return {
        refreshed: true,
        jwtCookie: responseCookies['JWT'],
        refreshTokenCookie: responseCookies['JWT-refresh-token']
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to refresh token: ${error.message}`, { cause: error })
      }
      throw new Error('Failed to refresh token: unknown error', { cause: error })
    }
  }
}
